import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import StoreLogo from '@prisjakt/ui-store-logo';

import WinnersDropdown from './components/winners-dropdown';
import ShopRating from '../shop-rating';

import {
  ShopOfTheYearNominationCategories,
  ShopOfTheYearNominationCategory,
} from '../../../../../../types';

import { getSortedCategories } from './helpers';
import { fetchSotyCategories } from './requests';

import * as S from './styles';

enum AwardType {
  CONSCIOUS = 'conscious',
}

type OwnProps = {
  categories?: ShopOfTheYearNominationCategories | null;
  className?: string;
  currentYear: number;
  winnerYear?: string;
};

const Winners = ({
  categories,
  className,
  currentYear,
  winnerYear,
}: OwnProps) => {
  const { t } = useContext(AppContext);

  const [visibleYear, setVisibleYear] = useState(
    Number(winnerYear) || currentYear
  );
  const [visibleCategories, setVisibleCategories] = useState<
    (ShopOfTheYearNominationCategory | null)[]
  >([]);

  // Sets sorting of categories on mount in useEffect, because sorting of categories
  // on render doesn't work in FF, where the elements for some reason gets mixed up with each other in the UI.
  useEffect(() => {
    if (!visibleCategories.length && categories?.nodes?.length) {
      const sortedCategories = getSortedCategories(categories.nodes);
      setVisibleCategories(sortedCategories);
    }
  }, []);

  useEffect(() => {
    fetchSotyCategories(visibleYear).then(response => {
      if (response.data?.shopOfTheYear?.categories?.nodes?.length > 0) {
        setVisibleCategories(
          getSortedCategories(response.data.shopOfTheYear.categories.nodes)
        );
      }
    });
  }, []);

  const sotyYears = [];
  for (let i = 2010; i <= currentYear; i += 1) sotyYears.push(i);

  return (
    <div className={className}>
      {visibleYear && (
        <WinnersDropdown
          visibleYear={visibleYear}
          onDropdownChange={(selectedCategories, year) => {
            setVisibleYear(year);
            const sortedCategories = selectedCategories?.nodes?.length
              ? getSortedCategories(selectedCategories.nodes)
              : [];

            setVisibleCategories(sortedCategories);
          }}
          sotyYears={sotyYears}
        />
      )}

      {visibleYear !== currentYear && (
        <div>
          <S.SubTitle>
            {t('campaigns', 'shop-of-the-year.past-winners')}
          </S.SubTitle>
          <p>{t('campaigns', 'shop-of-the-year.past-winners-description')}</p>
        </div>
      )}

      <S.WinnersWrapper>
        {visibleCategories?.map(category => {
          if (
            !category ||
            !category.name ||
            (visibleYear !== currentYear && !category.winner)
          )
            return null;

          return (
            <S.WinnerBox
              key={category.name}
              fullwidth={
                Boolean(category.mainAward) || Boolean(category.isSpecial)
              }
            >
              <S.Content>
                {category.logo && (
                  <S.Badge
                    src={category.logo}
                    year={visibleYear}
                    width={
                      Boolean(category.mainAward) || Boolean(category.isSpecial)
                        ? 128
                        : 88
                    }
                  />
                )}
                <S.TitleWrapper>
                  {category.name && (
                    <>
                      {category.winner ? (
                        <S.PreText>
                          {t('campaigns', 'shop-of-the-year.winners-category', {
                            year: visibleYear || '',
                          })}
                        </S.PreText>
                      ) : null}
                      <S.CategoryTitle>{category.name}</S.CategoryTitle>
                    </>
                  )}
                </S.TitleWrapper>
              </S.Content>
              {category.winner ? (
                <S.WinningShop>
                  <StoreLogo
                    name={category.winner.name}
                    storeId={category.winner.id}
                    featured
                    hasImage={Boolean(category.winner.hasLogo)}
                  />
                  {category.winner.userReviewSummary?.rating ? (
                    <ShopRating
                      rating={category.winner.userReviewSummary.rating}
                    />
                  ) : null}
                </S.WinningShop>
              ) : (
                <S.WinnerInfo>
                  <S.WinnerInfoText>
                    {t(
                      'campaigns',
                      'shop-of-the-year.winners-announced-december',
                      {
                        year: currentYear,
                        day: 11,
                      }
                    )}
                  </S.WinnerInfoText>
                  {category.resourceName === AwardType.CONSCIOUS ? (
                    <p>{t('campaigns', 'shop-of-the-year.conscious-info')}</p>
                  ) : (
                    <p>
                      {t('campaigns', 'shop-of-the-year.winners-review-info')}
                    </p>
                  )}
                </S.WinnerInfo>
              )}
            </S.WinnerBox>
          );
        })}
      </S.WinnersWrapper>
    </div>
  );
};

export default Winners;
